import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hinweise zur Namenszuordnung beim Erfassen von Statistiken aus Screenshots.
 *
 * Die OCR liest die Zahlen zuverlässig, der wunde Punkt ist der Abgleich des
 * Namens gegen den Kader. Zeilen, die dabei durchfallen, verschwanden früher
 * kommentarlos aus der Review-Tabelle — der Fehler fiel erst auf, wenn die
 * Punktsumme hinterher nicht zum Endstand passte. Dieser Kasten macht sie
 * sichtbar, die Punktprobe darunter fängt den Rest ab.
 */

/**
 * Sammelt für eine Mannschaft, was der Abgleich nicht sauber auflösen konnte.
 * Nur Zeilen mit Minuten zählen — Zeilen ohne werden beim Absenden ohnehin verworfen.
 *
 * @param {Object} params
 * @param {Array}  params.ocrPlayers - Zeilen aus dem Screenshot (mit playerId/matchConfidence)
 * @param {Array}  params.roster     - Kader der Mannschaft
 * @param {string} params.teamName   - Anzeigename der Mannschaft
 * @param {Array}  params.allPlayers - Alle Spieler, für die Auflösung kaderfremder Treffer
 * @param {Array}  [params.teams]    - Teamliste für den Namen des tatsächlichen Teams
 * @returns {{teamName: string, unmatched: Array, offRoster: Array, ambiguous: Array}}
 */
export const collectOcrMatchIssues = ({ ocrPlayers, roster, teamName, allPlayers = [], teams = [] }) => {
  const rosterIds = new Set(roster.map(p => p.id));
  const issues = { teamName, unmatched: [], offRoster: [], ambiguous: [] };

  (ocrPlayers || []).forEach(ocrPlayer => {
    if (!(ocrPlayer.stats?.minutes > 0)) return;

    const line = {
      originalName: ocrPlayer.originalName,
      playerName: ocrPlayer.playerName,
      minutes: ocrPlayer.stats.minutes,
      points: ocrPlayer.stats.points
    };

    if (!ocrPlayer.playerId) {
      issues.unmatched.push(line);
    } else if (!rosterIds.has(ocrPlayer.playerId)) {
      // Zugeordnet, aber laut Kader gehört der Spieler einem anderen Team —
      // meist ein Trade, der noch nicht im System steht.
      const dbPlayer = allPlayers.find(p => p.id === ocrPlayer.playerId);
      const actualTeam = teams.find(team => team.id === dbPlayer?.teamId);
      issues.offRoster.push({ ...line, actualTeam: actualTeam?.name || null });
    } else if (ocrPlayer.matchConfidence !== 'high') {
      issues.ambiguous.push(line);
    }
  });

  return issues;
};

/** true, wenn mindestens eine Mannschaft einen Hinweis hat */
export const hasOcrMatchIssues = (issuesList) =>
  (issuesList || []).some(i => i && (i.unmatched.length || i.offRoster.length || i.ambiguous.length));

/**
 * Warnkasten zur Namenszuordnung.
 * @param {{issues: Array}} props - eine Liste von collectOcrMatchIssues-Ergebnissen
 */
export const OcrMatchWarnings = ({ issues }) => {
  const { t } = useTranslation();

  if (!hasOcrMatchIssues(issues)) return null;

  const renderGroup = (key, lines, withPlayer) => {
    if (lines.length === 0) return null;
    return (
      <div className="mt-1">
        <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
          {t(`ocrReview.${key}`, { count: lines.length })}
        </p>
        <p className="text-xs text-amber-800 dark:text-amber-300">
          {t(`ocrReview.${key}Hint`)}
        </p>
        {lines.map((line, idx) => (
          <p key={idx} className="text-xs text-amber-900 dark:text-amber-200 ml-2">
            • <span className="font-mono">{line.originalName}</span>
            {withPlayer && <> → {line.playerName}{line.actualTeam ? ` (${line.actualTeam})` : ''}</>}
            {' '}· {t('ocrReview.lineStats', { minutes: line.minutes, points: line.points })}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 dark:border-amber-600 mb-4">
      <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200">
        ⚠️ {t('ocrReview.title')}
      </h3>
      <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
        {t('ocrReview.intro')}
      </p>
      {issues.map((teamIssues, teamIdx) => {
        if (!hasOcrMatchIssues([teamIssues])) return null;
        return (
          <div key={teamIdx} className="mt-3">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {teamIssues.teamName}
            </p>
            {renderGroup('unmatched', teamIssues.unmatched, false)}
            {renderGroup('offRoster', teamIssues.offRoster, true)}
            {renderGroup('ambiguous', teamIssues.ambiguous, true)}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Punktprobe: Summe der Statzeilen gegen den eingetragenen Endstand.
 * @param {{rows: Array<{name: string, sum: number, score: number}>}} props
 */
export const StatsScoreCheck = ({ rows }) => {
  const { t } = useTranslation();

  // Erst prüfen, wenn beide Endstände stehen und überhaupt Zeilen erfasst sind
  if (!rows || rows.some(r => isNaN(r.score))) return null;
  if (rows.every(r => r.sum === 0)) return null;

  const mismatches = rows.filter(r => r.sum !== r.score);

  if (mismatches.length === 0) {
    return (
      <div className="p-3 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-700 mb-4">
        <p className="text-sm text-green-800 dark:text-green-300">
          ✓ {t('ocrReview.scoreOk')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-700 mb-4">
      <p className="text-sm font-bold text-red-800 dark:text-red-300">
        ⚠️ {t('ocrReview.scoreCheckTitle')}
      </p>
      {mismatches.map((r, idx) => (
        <p key={idx} className="text-xs text-red-800 dark:text-red-300 ml-2">
          • {t('ocrReview.scoreMismatch', { team: r.name, sum: r.sum, score: r.score, diff: r.score - r.sum })}
        </p>
      ))}
    </div>
  );
};

/** Punktsumme der Zeilen, die beim Absenden tatsächlich gespeichert werden */
export const sumStatPoints = (stats) => Object.values(stats || {})
  .filter(s => s.minutes > 0)
  .reduce((total, s) => total + (parseInt(s.points, 10) || 0), 0);

/**
 * Punktprobe vor dem Absenden. Gibt die Zeilen zurueck, bei denen die Summe
 * der Statzeilen nicht zum Endstand passt — der Hinweiskasten oben ist nur
 * passiv, und genau so sind schon halbe Boxscores gespeichert worden.
 * @param {Array<{name: string, sum: number, score: number}>} rows
 * @returns {Array} abweichende Zeilen (leer, wenn alles passt oder kein Endstand steht)
 */
export const scoreMismatches = (rows) => (rows || [])
  .filter(r => !isNaN(r.score) && r.sum !== r.score);

export default OcrMatchWarnings;
